import {
  type FormElementRule,
  FormElementBase,
  FormElementConditionType,
  FormElementType,
  FormResponse,
  FormResponseStatus,
  LoginAccountRuleType,
  NameStampAnswerValue,
  RuleConditionBase,
  StatusBasedConditionType,
  TimeFrame,
  TimeFrameRuleType,
  Workflow,
} from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { emptyGuid } from "~/utils/constants";
import { getCurrentDateForTimeFrames } from "~/utils/dateUtils";
import { showError } from "~/utils/handleAPIError";
import {
  isFormElementBasedCondition,
  isFormStatusBasedCondition,
  isLoginAccountBasedCondition,
  isTimeFrameBasedCondition,
  isWorkflowStatusBasedCondition,
} from "~/utils/typeGuards";
import type { AnswerValue, FormResponseAnswerMap } from "./types";
import { isAnswerValid } from "./answerValidation";

/**
 * Determines if a form element rule should be applied based on its conditions
 *
 * A rule can have multiple conditions, and can be configured to be true if:
 * - ALL conditions are true (AND logic)
 * - ANY condition is true (OR logic)
 *
 * @param {FormElementRule} rule - The rule to evaluate
 * @param {FormResponseAnswerMap} answers - Current form answers
 * @param {FormElementBase[]} formElements - All form elements
 * @param {FormResponse} formResponse - Optional form response for status-based conditions
 * @param {Workflow} workflow - Optional workflow for workflow-status conditions
 * @returns {boolean} True if the rule should be applied
 */
export function getIsRuleApplicable(
  rule: FormElementRule,
  answers: FormResponseAnswerMap,
  formElements: FormElementBase[],
  formResponse?: FormResponse,
  workflow?: Workflow,
): boolean {
  // Rules with no conditions are never applicable
  if (rule.conditions.length === 0) {
    return false;
  }

  // Evaluate each condition and collect the ones that are true
  const applicableConditions = rule.conditions
    .map((c) =>
      getIsConditionApplicable(
        c,
        answers,
        formElements,
        formResponse,
        workflow,
      ),
    )
    .filter(Boolean);

  // Apply OR logic if trueIfAny is set, otherwise use AND logic
  if (rule.trueIfAny) {
    // OR logic: rule applies if ANY condition is true
    return applicableConditions.length > 0;
  }
  // AND logic: rule applies only if ALL conditions are true
  return applicableConditions.length === rule.conditions.length;
}

/**
 * Gets the answer value for a form element condition
 * @param condition - The condition to get the answer for
 * @param answers - Current form answers
 * @param formElements - All form elements
 * @returns The answer value and form element, or null if answer is hidden
 */
function getAnswerValueForCondition(
  condition: any,
  answers: FormResponseAnswerMap,
  formElements: FormElementBase[],
): { answerValue: any; formElement: FormElementBase | undefined } | null {
  const formElement = formElements.find(
    (el) => el.questionCode === condition.questionCode,
  );

  if (!condition.questionCode) {
    return { answerValue: undefined, formElement };
  }

  const repeatableGroupGuid = formElement?.repeaterGuid ?? emptyGuid;
  const answerKey = condition.questionCode + "-" + repeatableGroupGuid;

  // If the answer is hidden by another rule, don't apply this condition
  if (answers[answerKey]?.isHidden) {
    return null;
  }

  let answerValue = answers[answerKey]?.answerValue;

  // Special handling for cascader elements (convert array to string path)
  if (
    formElement?.type === FormElementType.CASCADER &&
    Array.isArray(answerValue)
  ) {
    answerValue = answerValue.map(String).join(" / ");
  }

  return { answerValue, formElement };
}

/**
 * Evaluates form element based conditions
 * @param condition - The form element condition to evaluate
 * @param answers - Current form answers
 * @param formElements - All form elements
 * @returns True if the condition is applicable
 */
function evaluateFormElementCondition(
  condition: any,
  answers: FormResponseAnswerMap,
  formElements: FormElementBase[],
): boolean {
  const result = getAnswerValueForCondition(condition, answers, formElements);

  if (result === null) {
    return (
      condition.type === FormElementConditionType.ANSWER_HAS_NO_VALUE ||
      condition.type === FormElementConditionType.ANSWER_NOT_EQUALS ||
      condition.type === FormElementConditionType.ANSWER_NOT_CONTAINS
    );
  }

  if (result.answerValue === null || result.answerValue === undefined) {
    return (
      condition.type === FormElementConditionType.ANSWER_HAS_NO_VALUE ||
      condition.type === FormElementConditionType.ANSWER_NOT_EQUALS ||
      condition.type === FormElementConditionType.ANSWER_NOT_CONTAINS
    );
  }

  const { answerValue, formElement } = result;

  switch (condition.type) {
    case FormElementConditionType.ANSWER_EQUALS:
      return getAnswerAsString(answerValue) === condition.answerValue;

    case FormElementConditionType.ANSWER_NOT_EQUALS:
      return getAnswerAsString(answerValue) !== condition.answerValue;

    case FormElementConditionType.ANSWER_CONTAINS:
      if (!condition.answerValue) return false;
      return getAnswerContains(answerValue, condition.answerValue);

    case FormElementConditionType.ANSWER_NOT_CONTAINS:
      if (!condition.answerValue) return false;
      return !getAnswerContains(answerValue, condition.answerValue);

    case FormElementConditionType.ANSWER_HAS_ANY_VALUE:
      if (formElement === undefined) return false;
      if (answerValue === 0) return false;
      return getAnswerHasAnyValue(answerValue, formElement.type);

    case FormElementConditionType.ANSWER_HAS_NO_VALUE:
      if (formElement === undefined) return false;
      if (answerValue === 0) return true;
      return (
        !getAnswerHasAnyValue(answerValue, formElement.type) ||
        !isAnswerValid(formElement, answerValue)
      );

    case FormElementConditionType.COUNT_EQUALS:
      if (formElement === undefined) return false;
      return (
        getFileUploadCount(answerValue) === (Number(condition.answerValue) || 0)
      );

    case FormElementConditionType.COUNT_NOT_EQUALS:
      if (formElement === undefined) return false;
      return (
        getFileUploadCount(answerValue) !== (Number(condition.answerValue) || 0)
      );

    default:
      showError(
        "Developer error: condition applicable not implemented for condition type: " +
          condition.type,
      );
      return false;
  }
}

/**
 * Evaluates workflow status based conditions
 * @param condition - The workflow status condition to evaluate
 * @param workflow - The workflow to check status against
 * @returns True if the condition is applicable
 */
function evaluateWorkflowStatusCondition(
  condition: any,
  workflow?: Workflow,
): boolean {
  const requiredStatus = condition.workflowStatus;
  const statusCondition = condition.type;

  switch (statusCondition) {
    case StatusBasedConditionType.STATUS_IS:
      return workflow?.status === requiredStatus;

    case StatusBasedConditionType.STATUS_IS_NOT:
      return workflow?.status !== requiredStatus;

    case StatusBasedConditionType.STATUS_IS_AFTER:
      return compareWorkflowStatuses(
        workflow,
        requiredStatus,
        (current, required) => current > required,
      );

    case StatusBasedConditionType.STATUS_IS_BEFORE:
      return compareWorkflowStatuses(
        workflow,
        requiredStatus,
        (current, required) => current < required,
      );

    default:
      return false;
  }
}

/**
 * Compares workflow statuses using a comparison function
 * @param workflow - The workflow containing status information
 * @param requiredStatus - The status to compare against
 * @param compareFn - Function to compare status indices
 * @returns True if the comparison passes
 */
function compareWorkflowStatuses(
  workflow: Workflow | undefined,
  requiredStatus: string,
  compareFn: (current: number, required: number) => boolean,
): boolean {
  const currentStatusIndex = getWorkflowStatusIndex(workflow, workflow?.status);
  const requiredStatusIndex = getWorkflowStatusIndex(workflow, requiredStatus);

  if (currentStatusIndex === undefined || requiredStatusIndex === undefined) {
    return false;
  }

  return compareFn(currentStatusIndex, requiredStatusIndex);
}

/**
 * Evaluates form status based conditions
 * @param condition - The form status condition to evaluate
 * @param formResponse - The form response to check status against
 * @returns True if the condition is applicable
 */
function evaluateFormStatusCondition(
  condition: any,
  formResponse?: FormResponse,
  workflow?: Workflow,
): boolean {
  const requiredStatus = condition.formStatus;
  const statusCondition = condition.type;
  const requiredForm = condition.formDefinitionGuid;

  // If requiredForm is set and does not match the current form, get status from workflow
  let statusToCheck = formResponse?.status;
  if (
    requiredForm &&
    formResponse?.formDefinitionGuid !== requiredForm &&
    workflow
  ) {
    const matchingFormResponse = workflow.formResponses?.find(
      (fr) => fr.formDefinitionGuid === requiredForm,
    );
    statusToCheck = matchingFormResponse?.status;
  }

  switch (statusCondition) {
    case StatusBasedConditionType.STATUS_IS:
      return statusToCheck === requiredStatus;

    case StatusBasedConditionType.STATUS_IS_NOT:
      return statusToCheck !== requiredStatus;

    case StatusBasedConditionType.STATUS_IS_AFTER:
      return compareFormStatuses(
        statusToCheck,
        requiredStatus,
        (current, required) => current > required,
      );

    case StatusBasedConditionType.STATUS_IS_BEFORE:
      return compareFormStatuses(
        statusToCheck,
        requiredStatus,
        (current, required) => current < required,
      );

    default:
      return false;
  }
}

/**
 * Compares form statuses using a comparison function
 * @param currentStatus - The current form status
 * @param requiredStatus - The status to compare against
 * @param compareFn - Function to compare status indices
 * @returns True if the comparison passes
 */
function compareFormStatuses(
  currentStatus: string | undefined,
  requiredStatus: string,
  compareFn: (current: number, required: number) => boolean,
): boolean {
  const currentStatusIndex = getFormResponseStatusIndex(currentStatus);
  const requiredStatusIndex = getFormResponseStatusIndex(requiredStatus);

  if (currentStatusIndex === undefined || requiredStatusIndex === undefined) {
    return false;
  }

  return compareFn(currentStatusIndex, requiredStatusIndex);
}

/**
 * Evaluates login account based conditions
 * @param condition - The login account condition to evaluate
 * @returns True if the condition is applicable
 */
function evaluateLoginAccountCondition(condition: any): boolean {
  const myInfoStore = useMyInfoStore();
  const requiredGroup = condition.orgGroupGuid;
  const requiredRole = condition.role;
  const statusCondition = condition.type;
  const currentUserGuid = myInfoStore.userInfo?.userGuid;

  if (!currentUserGuid) {
    return false;
  }

  const currentOrg = Object.values(myInfoStore.userInfo.sysOrgDict).find(
    (org) => org.guid === myInfoStore.currentSysOrgGuid,
  );

  // A condition targets either a role or a security group, never both. Fail closed
  // when it targets neither: "does not belong to <nothing>" would otherwise be true
  // for every user, silently hiding or locking elements for everyone.
  if (!requiredRole && !requiredGroup) {
    showError(
      "Developer error: login account condition targets neither a role nor a security group",
    );
    return false;
  }

  // Roles are the fixed set every org has; groups are org-defined and may not exist
  // at all. Role wins if both are somehow set, matching how the condition is built.
  const isMember = requiredRole
    ? (currentOrg?.roles?.includes(requiredRole) ?? false)
    : (currentOrg?.groupGuids?.includes(requiredGroup) ?? false);

  switch (statusCondition) {
    case LoginAccountRuleType.USER_BELONGS_TO:
      return isMember;

    case LoginAccountRuleType.USER_DOES_NOT_BELONG_TO:
      return !isMember;

    default:
      showError(
        "Developer error: condition applicable not implemented for login account condition type: " +
          statusCondition,
      );
      return false;
  }
}

/**
 * Evaluates time frame based conditions
 * @param condition - The time frame condition to evaluate
 * @returns True if the condition is applicable
 */
function evaluateTimeFrameCondition(
  condition: any,
  allTimeFrames: TimeFrame[],
): boolean {
  const myInfoStore = useMyInfoStore();
  const requiredTimeFrameCode = condition.timeFrameCode;
  const statusCondition = condition.type;
  const currentUserGuid = myInfoStore.userInfo?.userGuid;

  if (!currentUserGuid) {
    return false;
  }
  const timeFrame = allTimeFrames.find(
    (tf) => tf.code === requiredTimeFrameCode,
  );

  if (!timeFrame) {
    return false;
  }

  const isDefaultTimeFrame = !timeFrame.startDate && !timeFrame.endDate;

  const currentDate = getCurrentDateForTimeFrames();
  const parseDate = (date: unknown): Date | undefined => {
    if (!date) return undefined;
    if (typeof date === "object" && date !== null && "getTime" in date) {
      return date as Date;
    }
    return new Date(date as string);
  };

  const start = parseDate(timeFrame.startDate);
  const end = parseDate(timeFrame.endDate);

  const within =
    (start ? currentDate >= start : true) && (end ? currentDate <= end : true);

  switch (statusCondition) {
    case TimeFrameRuleType.WITHIN_TIME_FRAME:
      return isDefaultTimeFrame ? true : within;

    case TimeFrameRuleType.NOT_WITHIN_TIME_FRAME:
      return isDefaultTimeFrame ? false : !within;

    default:
      showError(
        "Developer error: condition applicable not implemented for time frame condition type: " +
          statusCondition,
      );
      return false;
  }
}

/**
 * Evaluates if a single rule condition is applicable based on its type and parameters
 *
 * Supports multiple condition types:
 * - Form element based conditions (comparing answer values)
 * - Form status based conditions
 * - Workflow status based conditions
 * - Login account based conditions
 *
 * @param {RuleConditionBase} condition - The condition to evaluate
 * @param {FormResponseAnswerMap} answers - Current form answers
 * @param {FormElementBase[]} formElements - All form elements
 * @param {FormResponse} formResponse - Optional form response for status-based conditions
 * @param {Workflow} workflow - Optional workflow for workflow-status conditions
 * @returns {boolean} True if the condition is applicable
 */
export function getIsConditionApplicable(
  condition: RuleConditionBase,
  answers: FormResponseAnswerMap,
  formElements: FormElementBase[],
  formResponse?: FormResponse,
  workflow?: Workflow,
): boolean {
  if (isFormElementBasedCondition(condition)) {
    return evaluateFormElementCondition(condition, answers, formElements);
  }

  if (isWorkflowStatusBasedCondition(condition)) {
    return evaluateWorkflowStatusCondition(condition, workflow);
  }

  if (isFormStatusBasedCondition(condition)) {
    return evaluateFormStatusCondition(condition, formResponse, workflow);
  }

  if (isLoginAccountBasedCondition(condition)) {
    return evaluateLoginAccountCondition(condition);
  }

  if (isTimeFrameBasedCondition(condition)) {
    return evaluateTimeFrameCondition(
      condition,
      workflow?.workflowDefinition?.timeFrames ?? [],
    );
  }

  throw new Error("Developer error: condition type not implemented");
}

/**
 * Safely converts an array item to string representation
 * @param item - The item to convert
 * @returns String representation of the item
 */
function stringifyArrayItem(item: unknown): string {
  try {
    return JSON.stringify(item);
  } catch {
    return String(item);
  }
}

/**
 * Converts an array to a fallback string representation
 * @param answer - The array to convert
 * @returns String representation of the array
 */
function arrayToFallbackString(answer: unknown[]): string {
  try {
    const items = answer.map(stringifyArrayItem).join(", ");
    return `[${items}]`;
  } catch {
    return `[Array with ${answer.length} items]`;
  }
}

/**
 * Safely converts an object key-value pair to string
 * @param key - The key
 * @param value - The value
 * @returns String representation of the key-value pair
 */
function stringifyObjectPair(key: string, value: unknown): string {
  try {
    return `${key}: ${JSON.stringify(value)}`;
  } catch {
    return `${key}: ${String(value)}`;
  }
}

/**
 * Converts an object to a fallback string representation
 * @param answer - The object to convert
 * @returns String representation of the object
 */
function objectToFallbackString(answer: object): string {
  try {
    const entries = Object.entries(answer);
    if (entries.length === 0) {
      return "{}";
    }
    const pairs = entries
      .slice(0, 3)
      .map(([key, value]) => stringifyObjectPair(key, value));
    const suffix = entries.length > 3 ? ", ..." : "";
    return `{${pairs.join(", ")}${suffix}}`;
  } catch {
    // Last resort: use constructor name if available
    return answer.constructor?.name || "[object Object]";
  }
}

/**
 * Handles object conversion with fallback strategies
 * @param answer - The object to convert
 * @returns String representation of the object
 */
function handleObjectConversion(answer: object): string {
  try {
    return JSON.stringify(answer);
  } catch {
    // Fallback for objects that can't be JSON stringified (e.g., circular references)
    return Array.isArray(answer)
      ? arrayToFallbackString(answer)
      : objectToFallbackString(answer);
  }
}

/**
 * Converts an answer value to a string representation suitable for comparison
 *
 * This function handles different answer types appropriately:
 * - Objects: JSON stringified
 * - Arrays: JSON stringified
 * - Primitives: String conversion
 * - null/undefined: Empty string
 *
 * @template TAnswerType - The form element type
 * @param {AnswerValue<TAnswerType> | undefined | null} answer - The answer to convert
 * @returns {string} String representation of the answer
 */
export function getAnswerAsString<TAnswerType extends FormElementType>(
  answer: AnswerValue<TAnswerType> | undefined | null,
): string {
  if (answer === null || answer === undefined) {
    return "";
  }

  // Handle arrays and objects by JSON stringifying them
  if (typeof answer === "object") {
    return handleObjectConversion(answer);
  }

  // For primitives, use standard string conversion
  return String(answer);
}

/**
 * Checks if an answer contains a specific substring
 *
 * @template TAnswerType - The form element type
 * @param {AnswerValue<TAnswerType> | undefined | null} answer - The answer to check
 * @param {string} expectedAnswerRaw - The substring to look for
 * @returns {boolean} True if the answer contains the substring
 */
export function getAnswerContains<TAnswerType extends FormElementType>(
  answer: AnswerValue<TAnswerType> | undefined | null,
  expectedAnswerRaw: string,
): boolean {
  return getAnswerAsString(answer).includes(expectedAnswerRaw);
}

/**
 * Gets the count of items in a file upload answer
 *
 * @template TAnswerType - The form element type
 * @param {AnswerValue<TAnswerType> | undefined | null} answer - The answer to count items in
 * @returns {number} The number of items in the answer (0 if not an array or empty)
 */
export function getFileUploadCount<TAnswerType extends FormElementType>(
  answer: AnswerValue<TAnswerType> | undefined | null,
): number {
  if (!answer || !Array.isArray(answer)) {
    return 0;
  }
  return answer.length;
}

/**
 * Determines if an answer has any value based on its element type
 *
 * Different element types have different criteria for what constitutes a "value":
 * - Simple text: Must be a non-empty string
 * - Name stamp: Must have at least one non-revoked stamp
 * - Other types: Any non-null/undefined value is considered a value
 *
 * @template TAnswerType - The form element type
 * @param {AnswerValue<TAnswerType> | undefined | null} answer - The answer to check
 * @param {FormElementType} elementType - The type of form element
 * @returns {boolean} True if the answer has a value according to its type's criteria
 */
export function getAnswerHasAnyValue<TAnswerType extends FormElementType>(
  answer: AnswerValue<TAnswerType> | undefined | null,
  elementType: FormElementType,
): boolean {
  // Quick check for empty values that apply to all element types
  if (answer === undefined || answer === null || Number.isNaN(answer)) {
    return false;
  }

  // Element-type specific checks
  switch (elementType) {
    case FormElementType.SIMPLE_TEXT:
      // For text elements, empty strings are considered "no value"
      return answer !== "";

    case FormElementType.RICH_TEXT: {
      // For rich text elements, strip HTML and check if resulting text is empty
      if (typeof answer !== "string") {
        return false;
      }
      const text = answer as string;
      if (text.trim() === "") {
        return false;
      }
      // Strip HTML tags and decode HTML entities to get plain text
      const noHtml = text.replace(/<[^>]*>/g, "");
      const decodedText = noHtml.replace(/&[a-zA-Z0-9#]+;/g, "");
      return decodedText.trim() !== "";
    }

    case FormElementType.NAME_STAMP: {
      // For name stamps, we need at least one non-revoked stamp
      if (!Array.isArray(answer)) {
        return false;
      }
      const stamps = answer as NameStampAnswerValue[];
      return stamps.some((s) => !s.revoked);
    }
  }

  // For all other element types, any value that passes the initial check is considered valid
  return true;
}

/**
 * Gets the index of a form response status in the status sequence
 *
 * This is used to determine if one status comes before or after another
 *
 * @param {string | null | undefined} status - The status to find the index of
 * @returns {number | undefined} The index of the status, or undefined if not found
 */
export function getFormResponseStatusIndex(
  status?: string | null,
): number | undefined {
  if (!status) {
    return undefined;
  }
  // Get all possible form response statuses in their defined order
  const statusArr = Object.values(FormResponseStatus);
  return statusArr.indexOf(status as FormResponseStatus);
}

/**
 * Gets the index of a workflow status in the workflow's status sequence
 *
 * This is used to determine if one status comes before or after another
 *
 * @param {Workflow | undefined} workflow - The workflow containing the status list
 * @param {string | null | undefined} status - The status to find the index of
 * @returns {number | undefined} The index of the status, or undefined if not found
 */
export function getWorkflowStatusIndex(
  workflow?: Workflow,
  status?: string | null,
): number | undefined {
  if (!workflow || !status) {
    showError("Cannot apply workflow status condition without a workflow");
    return undefined;
  }

  // Get the list of status codes from the workflow definition
  const statusArr = workflow.workflowDefinition?.statusList.map((s) => s.code);

  if (!statusArr) {
    showError("Status list is missing in the workflow definition");
    return undefined;
  }

  // Find the index of the status in the list
  return statusArr.indexOf(status);
}
